/**
 * Membership Manager
 * Handles membership state, popup interactions, and cart synchronization
 */

import { ThemeEvents, CartAddEvent } from '@theme/events';

const SPEND_CAP = 250;
const MEMBERSHIP_HANDLES = ['membership', '28-day-membership', 'annual-membership-upgrade'];
const DEBUG = false;
const debugLog = (...args) => { if (DEBUG) console.log(...args); };

class MembershipManager {
  constructor() {
    this.membershipActive = false;
    this.membershipProductHandle = window.Shopify?.theme?.settings?.membership_product_handle || 'membership';
    this.membership28DayHandle = window.Shopify?.theme?.settings?.membership_28day_product_handle || '28-day-membership';
    this.membershipUpgradeHandle = window.Shopify?.theme?.settings?.membership_upgrade_product_handle || 'annual-membership-upgrade';
    this.allowNextAddToCart = false;
    this.loadMembershipState();
    // Sync the product form's variant id to Non-Member immediately for obvious non-members,
    // before the async init() (which awaits a /cart.js fetch) has a chance to run. This
    // closes the race window where a fast click on Add to Cart submits the Member variant
    // because Shopify rendered the form's hidden id from a Member-variant URL.
    this.eagerNonMemberFormSync();
    this.init();
  }

  eagerNonMemberFormSync() {
    if (window.customerHasActiveMemberTag === true) return;
    const nonMemberCard = document.querySelector('[data-membership-card="non-member"]');
    const memberCard = document.querySelector('[data-membership-card="member"]');
    const nonMemberVariantId = nonMemberCard?.dataset.variantId;
    const memberVariantId = memberCard?.dataset.variantId;
    const variantHidden = document.querySelector('input[type="hidden"][name="id"]');
    if (nonMemberVariantId && memberVariantId && variantHidden && variantHidden.value === memberVariantId) {
      variantHidden.value = nonMemberVariantId;
      variantHidden.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  loadMembershipState() {
    const state = window.customerMembershipState || {};
    this.membershipType = state.membershipType || 'annual';
    // clothing_spend is stored as single_line_text (e.g. "0.00", "125.50"); parse to number
    this.clothingSpend = parseFloat(state.clothingSpend) || 0;
    this.membershipExpiry = state.membershipExpiry ? new Date(state.membershipExpiry) : null;
    this.upgradeWindowEnd = state.upgradeWindowEnd ? new Date(state.upgradeWindowEnd) : null;
    this.hasPurchased28Day = state.hasPurchased28Day === true;
    this.variantIds = window.membershipProductVariantIds || { upgrade: [], day28: [] };
    const now = new Date();
    const hasActiveTag = window.customerHasActiveMemberTag === true;
    this.inUpgradeWindow = !hasActiveTag && this.hasPurchased28Day && this.upgradeWindowEnd && this.upgradeWindowEnd > now;
    this.spendCapExceeded = hasActiveTag && this.membershipType === '28-day' && this.clothingSpend >= SPEND_CAP;
    this.effectiveSpendCapExceeded = this.spendCapExceeded; // Updated by updateRemainingSpendDisplay when cart data available
    this.hasUpgradeInCart = false; // When true, spending limits are suspended
    this.has28DayMembershipInCart = false; // When true, treat as 28-day for cap (pre-purchase)
  }

  async init() {
    debugLog('[MembershipManager] Initializing...');

    try {
      // Intercept fetch requests FIRST (before anything else)
      this.interceptFetchRequests();

      // Detect initial membership status
      await this.detectMembershipStatus();
      debugLog('[MembershipManager] Initial membership status:', this.membershipActive);

      // Initialize UI
      this.initializeCardSelectors();
      this.initializePopup();
      this.initializeAddToCart();
      this.initializeCheckoutBlocking();

      // Update UI based on initial membership status
      this.updateUIBasedOnMembership();
      this.updateRemainingSpendDisplay();

      // Validate cart immediately on load
      await this.validateCartNow();

      this.updateCheckoutButtonState();

      // Listen for cart updates
      this.listenForCartUpdates();

      debugLog('[MembershipManager] Initialization complete');
    } finally {
      // Re-enable add-to-cart / accelerated checkout. The class is set on <html> by
      // layout/theme.liquid for product pages; we remove it once init is done so the user
      // can interact even if something above threw.
      document.documentElement.classList.remove('membership-pending');
    }
  }

  interceptFetchRequests() {
    const self = this;
    let lastCartAddAllowedAt = 0;
    const CART_ADD_COOLDOWN_MS = 1500;

    const checkCartAdd = function(body) {
      const variantIds = self.getVariantIdsFromCartAddBody(body);
      if (self.allowNextAddToCart) {
        debugLog('[MembershipManager] fetch /cart/add: ALLOWED (allowNextAddToCart)', { variantIds });
        self.allowNextAddToCart = false;
        onCartAddAllowed();
        return { allow: true };
      }

      // Rate limit: prevent rapid duplicate adds (e.g. cascade from cart:update handling)
      const now = Date.now();
      if (now - lastCartAddAllowedAt < CART_ADD_COOLDOWN_MS) {
        debugLog('[MembershipManager] fetch /cart/add: BLOCKED (rate limit)', { variantIds, msSinceLast: now - lastCartAddAllowedAt });
        return { allow: false };
      }

      for (const vid of variantIds) {
        if (self.isUpgradeVariant(vid)) {
          if (!self.hasPurchased28Day) {
            alert("The £20 upgrade is only available to customers who have had a 28-day membership.");
            return { allow: false };
          }
          const now = new Date();
          if (!self.upgradeWindowEnd || self.upgradeWindowEnd < now) {
            alert("Your upgrade window has expired. Please purchase a new annual membership at full price.");
            return { allow: false };
          }
        }
        if (self.is28DayVariant(vid)) {
          if (self.hasPurchased28Day) {
            alert("You've already used your 28-day membership trial. Choose the annual membership instead.");
            return { allow: false };
          }
        }
      }

      const memberCard = document.querySelector('[data-membership-card="member"]');
      if (memberCard) {
        const radio = memberCard.querySelector('input[type="radio"]');
        // Also check if the variant id actually being submitted IS the page's Member variant.
        // The radio state can lag the form's hidden id input — e.g. when Shopify rendered the
        // form with a Member-variant URL param and the JS sync hasn't run yet, or when another
        // form on the page submits the wrong id.
        const memberVariantId = memberCard.dataset.variantId;
        const submittingMemberVariant = memberVariantId && variantIds.some(vid => String(vid) === String(memberVariantId));
        const memberSelected = (radio && radio.checked) || submittingMemberVariant;
        if (memberSelected && !self.membershipActive) {
          debugLog('[MembershipManager] fetch /cart/add: BLOCKED (non-member adding member variant)', { radioChecked: radio?.checked, submittingMemberVariant });
          if (self.inUpgradeWindow) {
            self.showPopup('upgrade');
          } else {
            self.showPopup('membership-selection');
          }
          return { allow: false };
        }
        if (memberSelected && !self.hasUpgradeInCart && (self.spendCapExceeded || self.effectiveSpendCapExceeded)) {
          debugLog('[MembershipManager] fetch /cart/add: BLOCKED (at spend cap)');
          if (self.shouldShowUpgradeMessaging()) {
            self.showPopup('upgrade');
          } else {
            alert("Your 28-day membership includes £250 of member-priced clothing. You've reached that limit for this order.");
          }
          return { allow: false };
        }
      }

      debugLog('[MembershipManager] fetch /cart/add: ALLOWED (passed all checks)', { variantIds });
      onCartAddAllowed();
      return { allow: true };
    };

    const onCartAddAllowed = function() {
      lastCartAddAllowedAt = Date.now();
    };

    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      const urlStr = (typeof url === 'string' ? url : (url && url.url) || '').toString();
      if (urlStr.includes('/cart/add')) {
        const body = options && options.body;
        debugLog('[MembershipManager] fetch /cart/add intercepted', { url: urlStr, hasBody: !!body });
        const result = checkCartAdd(body);
        if (!result.allow) {
          debugLog('[MembershipManager] fetch /cart/add: rejecting (blocked)');
          return Promise.reject(new Error('Membership check failed'));
        }
        debugLog('[MembershipManager] fetch /cart/add: proceeding');
      }
      if (urlStr.includes('/cart/change') && options && options.body && self.membershipActive) {
        let payload;
        try {
          payload = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        } catch (_) {}
        if (payload && typeof payload.line === 'number' && typeof payload.quantity === 'number') {
          return (async () => {
            const cart = await fetch('/cart.js').then(r => r.json());
            const hasUpgrade = cart.items.some(i => i.handle === self.membershipUpgradeHandle);
            if (hasUpgrade) {
              return originalFetch.apply(this, arguments);
            }
            const has28Day = cart.items.some(i => i.handle === self.membership28DayHandle);
            const effectiveIs28Day = self.membershipType === '28-day' || has28Day;
            if (!effectiveIs28Day) {
              return originalFetch.apply(this, arguments);
            }
            const baseSpend = (has28Day && self.membershipType !== '28-day') ? 0 : (self.clothingSpend || 0);
            const line = payload.line;
            const requestedQty = Math.max(0, parseInt(String(payload.quantity), 10) || 1);
            const lineItem = cart.items[line - 1];
            if (lineItem && !self.isMembershipItem(lineItem)) {
              const currentQty = lineItem.quantity;
              const unitPrice = currentQty > 0 ? lineItem.final_line_price / (100 * currentQty) : 0;
              const otherQualifying = cart.items
                .filter(i => !self.isMembershipItem(i) && i.key !== lineItem.key)
                .reduce((s, i) => s + i.final_line_price / 100, 0);
              const requestedLineTotal = unitPrice * requestedQty;
              if (baseSpend + otherQualifying + requestedLineTotal > SPEND_CAP) {
                const headroom = Math.max(0, SPEND_CAP - baseSpend - otherQualifying);
                const maxQty = unitPrice > 0 ? Math.floor(headroom / unitPrice) : 0;
                if (self.shouldShowUpgradeMessaging()) self.showPopup('upgrade');
                const reducedPayload = { ...payload, quantity: maxQty };
                const modifiedOptions = { ...options, body: JSON.stringify(reducedPayload) };
                return originalFetch.call(this, url, modifiedOptions);
              }
            }
            return originalFetch.apply(this, arguments);
          })();
        }
      }
      const promise = originalFetch.apply(this, arguments);
      if (urlStr.includes('/cart/add') || urlStr.includes('/cart/change')) {
        promise.then((response) => {
          if (response.ok) {
            requestAnimationFrame(() => {
              self.updateRemainingSpendDisplay().then(() => self.ensureCartUnderCap());
            });
          }
          return response;
        });
      }
      return promise;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._membershipUrl = url;
      return originalOpen.apply(this, [method, url, ...args]);
    };

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function(body) {
      const url = this._membershipUrl || '';
      if (url.includes('/cart/add')) {
        debugLog('[MembershipManager] XHR /cart/add intercepted');
        const result = checkCartAdd(body);
        if (!result.allow) {
          debugLog('[MembershipManager] XHR /cart/add: aborting (blocked)');
          this.abort();
          return;
        }
      }
      return originalSend.apply(this, arguments);
    };
  }

  async wouldCartExceedSpendCap() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const qualifyingTotal = cart.items
        .filter(item => !this.isMembershipItem(item))
        .reduce((sum, item) => sum + item.final_line_price / 100, 0);
      return this.clothingSpend + qualifyingTotal > SPEND_CAP;
    } catch {
      return false;
    }
  }

  async detectMembershipStatus() {
    // 1. Check global customer tag first (available on all pages via theme.liquid)
    // This is the most reliable source of truth and works on all pages
    if (window.customerHasActiveMemberTag === true) {
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');
      debugLog('[MembershipManager] Customer has active-member tag');
      return;
    }

    // 2. Check data-membership-state element (only on product pages with membership selector)
    const membershipStateEl = document.querySelector('[data-membership-state]');
    if (membershipStateEl && membershipStateEl.value === 'true') {
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');
      debugLog('[MembershipManager] Found membership-state element');
      return;
    }

    // 3. Check sessionStorage (fast cache to prevent race conditions during page transitions)
    const cachedStatus = sessionStorage.getItem('membershipActive');
    if (cachedStatus === 'true') {
      // Use cached value temporarily, but still verify with cart check
      this.membershipActive = true;
    }

    // 4. Check cart for membership product (async, but most accurate for cart-based membership)
    const hasInCart = await this.checkCartForMembership();
    this.membershipActive = hasInCart;
    sessionStorage.setItem('membershipActive', hasInCart ? 'true' : 'false');
    
    if (hasInCart) {
      debugLog('[MembershipManager] Membership found in cart');
    } else {
      debugLog('[MembershipManager] No membership found');
    }
  }

  isMembershipHandle(handle) {
    return handle === this.membershipProductHandle ||
           handle === this.membership28DayHandle ||
           handle === this.membershipUpgradeHandle;
  }

  isMembershipItem(item) {
    return item.product_type === 'Membership' ||
           (item.tags && item.tags.includes('site-membership')) ||
           this.isMembershipHandle(item.handle);
  }

  effectiveIs28Day() {
    return this.membershipType === '28-day' || this.has28DayMembershipInCart;
  }

  shouldShowUpgradeMessaging() {
    return this.membershipType === '28-day';
  }

  async checkCartForMembership() {
    if (window.customerHasActiveMemberTag === true) {
      return true;
    }

    const membershipStateEl = document.querySelector('[data-membership-state]');
    if (membershipStateEl && membershipStateEl.value === 'true') {
      return true;
    }

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.items.some(item => this.isMembershipItem(item));
    } catch (error) {
      console.error('[ERROR] Check cart failed:', error);
      return false;
    }
  }

  getVariantIdsFromCartAddBody(body) {
    const ids = [];
    if (!body) return ids;
    try {
      if (body instanceof FormData) {
        const id = body.get('id');
        if (id) ids.push(parseInt(id));
      } else if (typeof body === 'string') {
        if (body.startsWith('{')) {
          const data = JSON.parse(body);
          if (data.items && Array.isArray(data.items)) {
            data.items.forEach(i => { if (i.id) ids.push(parseInt(i.id)); });
          } else if (data.id) ids.push(parseInt(data.id));
        } else {
          const params = new URLSearchParams(body);
          if (params.has('id')) ids.push(parseInt(params.get('id')));
        }
      }
    } catch (e) {}
    return ids;
  }

  isUpgradeVariant(variantId) {
    return this.variantIds.upgrade && this.variantIds.upgrade.includes(variantId);
  }

  is28DayVariant(variantId) {
    return this.variantIds.day28 && this.variantIds.day28.includes(variantId);
  }

  initializeCardSelectors() {
    const cards = document.querySelectorAll('[data-membership-card]');

    cards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('membership-pill--disabled')) {
          e.preventDefault();
          return;
        }

        const radio = card.querySelector('input[type="radio"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          this.handleCardSelection(card);
        }
      });
    });
  }

  handleCardSelection(selectedCard) {
    const container = selectedCard.closest('[data-membership-selector]');
    const allCards = container.querySelectorAll('[data-membership-card]');

    // Update visual states
    allCards.forEach(card => {
      card.classList.remove('membership-pill--selected');
    });
    selectedCard.classList.add('membership-pill--selected');

    // Update variant selection
    const variantId = selectedCard.dataset.variantId;
    if (variantId) {
      this.updateSelectedVariant(variantId);
    }
  }

  updateSelectedVariant(variantId) {
    // Find and trigger the main variant selector
    const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
    if (variantInput) {
      variantInput.checked = true;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // If no radio input, update select or hidden input
      const variantSelect = document.querySelector('select[name="id"]');
      const variantHidden = document.querySelector('input[type="hidden"][name="id"]');

      if (variantSelect) {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (variantHidden) {
        variantHidden.value = variantId;
        variantHidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  initializePopup() {
    const popup = document.querySelector('[data-membership-popup]');
    if (!popup) return;

    // Close button
    const closeBtn = popup.querySelector('[data-membership-popup-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup(true));
    }

    // Overlay click
    const overlay = popup.querySelector('[data-membership-popup-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.closePopup(true));
    }

    // Action buttons
    const actions = popup.querySelectorAll('[data-membership-action]');
    actions.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.membershipAction;
        this.handlePopupAction(action);
      });
    });
  }

  showPopup(mode = 'membership-selection') {
    const popup = document.querySelector('[data-membership-popup]');
    if (popup) {
      popup.dataset.membershipPopupMode = mode;
      popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
      const selectionPanel = popup.querySelector('[data-membership-popup-selection]');
      const upgradePanel = popup.querySelector('[data-membership-popup-upgrade]');
      if (selectionPanel) selectionPanel.style.display = mode === 'membership-selection' ? '' : 'none';
      if (upgradePanel) {
        upgradePanel.style.display = mode === 'upgrade' ? '' : 'none';
        const capMsg = upgradePanel.querySelector('[data-upgrade-message="cap"]');
        const expiredMsg = upgradePanel.querySelector('[data-upgrade-message="expired"]');
        if (capMsg && expiredMsg) {
          capMsg.style.display = (this.spendCapExceeded || this.effectiveSpendCapExceeded) ? '' : 'none';
          expiredMsg.style.display = this.inUpgradeWindow ? '' : 'none';
        }
      }
    }
  }

  closePopup(switchToRegular = false) {
    const popup = document.querySelector('[data-membership-popup]');
    if (popup) {
      popup.style.display = 'none';
      document.body.style.overflow = '';
    }

    if (switchToRegular) {
      // Select non-member card
      const nonMemberCard = document.querySelector('[data-membership-card="non-member"]');
      if (nonMemberCard) {
        const radio = nonMemberCard.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          this.handleCardSelection(nonMemberCard);
        }
      }
    }
  }

  async handlePopupAction(action) {
    switch (action) {
      case 'add-membership':
      case 'add-annual':
        await this.addMembershipAndProduct('annual');
        break;
      case 'add-28day':
        await this.addMembershipAndProduct('28-day');
        break;
      case 'add-upgrade':
        await this.addUpgradeAndProduct();
        break;
      case 'login':
        this.redirectToLogin();
        break;
      case 'switch-to-regular':
        this.closePopup(true);
        break;
    }
  }

  async addMembershipAndProduct(membershipType = 'annual') {
    sessionStorage.setItem('skipValidation', Date.now().toString());

    const actionId = membershipType === '28-day' ? 'add-28day' : 'add-annual';
    const addButton = document.querySelector(`[data-membership-action="${actionId}"]`);
    const originalText = addButton ? addButton.textContent : '';

    const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${timeout}ms`)), timeout)
      );
      return Promise.race([fetch(url, options), timeoutPromise]);
    };

    try {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Adding to cart...';
        addButton.style.opacity = '0.6';
        addButton.style.cursor = 'not-allowed';
      }

      const handle = membershipType === '28-day' ? this.membership28DayHandle : this.membershipProductHandle;
      console.log('[DEBUG] Fetching membership product:', handle);
      const membershipProduct = await fetchWithTimeout(`/products/${handle}.js`)
        .then(r => r.json());

      console.log('[DEBUG] Step 2: Getting variant ID...');
      const memberCard = document.querySelector('[data-membership-card="member"]');
      const productVariantId = memberCard ? memberCard.dataset.variantId : null;

      if (!productVariantId) {
        throw new Error('Could not find product variant');
      }

      console.log('[DEBUG] Step 3: Fetching current cart...');
      const currentCart = await fetchWithTimeout('/cart.js').then(r => r.json());
      console.log('[DEBUG] Current cart has', currentCart.items.length, 'items');

      // Build items list
      const itemsToAdd = [
        { id: membershipProduct.variants[0].id, quantity: 1 },
        { id: parseInt(productVariantId), quantity: 1 }
      ];

      // Convert existing non-member items (limit to prevent hanging)
      console.log('[DEBUG] Step 4: Converting existing items...');
      let converted = 0;
      for (const item of currentCart.items) {
        if (item.product_type === 'Membership' || (item.tags && item.tags.includes('site-membership'))) {
          continue;
        }

        const isNonMember = item.variant_title && item.variant_title.includes('Non-Member');

        if (isNonMember) {
          try {
            console.log('[DEBUG] Converting:', item.title);
            const product = await fetchWithTimeout(`/products/${item.handle}.js`, {}, 5000).then(r => r.json());
            const memberVariant = product.variants.find(v => {
              const targetTitle = item.variant_title.replace(' / Non-Member', ' / Member');
              return v.title === targetTitle;
            });

            if (memberVariant) {
              itemsToAdd.push({ id: memberVariant.id, quantity: item.quantity });
              converted++;
            } else {
              itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
            }
          } catch (err) {
            console.warn('[DEBUG] Failed to convert item, keeping original:', err);
            itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
          }
        } else {
          itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
        }
      }
      console.log('[DEBUG] Converted', converted, 'items. Total items to add:', itemsToAdd.length);

      // Clear and add
      console.log('[DEBUG] Step 5: Clearing cart...');
      await fetchWithTimeout('/cart/clear.js', { method: 'POST' });

      console.log('[DEBUG] Step 6: Adding all items...');
      this.allowNextAddToCart = true;
      await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      console.log('[DEBUG] Step 7: Items added successfully');

      // Update state
      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');

      this.closePopup(false);
      window.location.href = '/cart';

    } catch (error) {
      console.error('[ERROR] Add membership failed:', error.message);
      alert(`Error: ${error.message}. Please try again.`);

      sessionStorage.removeItem('skipValidation');

      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = originalText;
        addButton.style.opacity = '';
        addButton.style.cursor = '';
      }
    }
  }

  async addUpgradeAndProduct() {
    sessionStorage.setItem('skipValidation', Date.now().toString());

    const addButton = document.querySelector('[data-membership-action="add-upgrade"]');
    const originalText = addButton ? addButton.textContent : '';

    const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${timeout}ms`)), timeout)
      );
      return Promise.race([fetch(url, options), timeoutPromise]);
    };

    try {
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Adding to cart...';
        addButton.style.opacity = '0.6';
        addButton.style.cursor = 'not-allowed';
      }

      const upgradeProduct = await fetchWithTimeout(`/products/${this.membershipUpgradeHandle}.js`).then(r => r.json());
      const memberCard = document.querySelector('[data-membership-card="member"]');
      const productVariantId = memberCard ? memberCard.dataset.variantId : null;

      if (!productVariantId) throw new Error('Could not find product variant');

      const currentCart = await fetchWithTimeout('/cart.js').then(r => r.json());

      const itemsToAdd = [
        { id: upgradeProduct.variants[0].id, quantity: 1 },
        { id: parseInt(productVariantId), quantity: 1 }
      ];

      for (const item of currentCart.items) {
        if (this.isMembershipItem(item)) continue;
        const isNonMember = item.variant_title && item.variant_title.includes('Non-Member');
        if (isNonMember) {
          try {
            const product = await fetchWithTimeout(`/products/${item.handle}.js`, {}, 5000).then(r => r.json());
            const memberVariant = product.variants.find(v => {
              const targetTitle = item.variant_title.replace(' / Non-Member', ' / Member');
              return v.title === targetTitle;
            });
            if (memberVariant) {
              itemsToAdd.push({ id: memberVariant.id, quantity: item.quantity });
            } else {
              itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
            }
          } catch {
            itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
          }
        } else {
          itemsToAdd.push({ id: item.variant_id, quantity: item.quantity });
        }
      }

      await fetchWithTimeout('/cart/clear.js', { method: 'POST' });
      this.allowNextAddToCart = true;
      await fetchWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      this.membershipActive = true;
      sessionStorage.setItem('membershipActive', 'true');
      this.closePopup(false);
      window.location.href = '/cart';

    } catch (error) {
      console.error('[ERROR] Add upgrade failed:', error.message);
      alert(`Error: ${error.message}. Please try again.`);
      sessionStorage.removeItem('skipValidation');
      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = originalText;
        addButton.style.opacity = '';
        addButton.style.cursor = '';
      }
    }
  }

  redirectToLogin() {
    const currentUrl = window.location.pathname;
    window.location.href = `/account/login?return_url=${encodeURIComponent(currentUrl)}`;
  }

  initializeAddToCart() {
    // Intercept CLICK first - preventing it stops the form submit, so the theme never runs its fetch.
    // This prevents the double-add (theme fetch + our fetch).
    document.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('button[type="submit"][name="add"], button[type="submit"][data-add-to-cart], [data-type="add-to-cart-form"] button[type="submit"]');
      const form = btn?.closest?.('form');
      const isProductForm = form && (
        (form.getAttribute?.('action')?.includes?.('/cart/add')) ||
        form.closest?.('[data-product-form]') ||
        form.closest?.('[data-type="add-to-cart-form"]')
      );
      if (!isProductForm || !btn) return;

      const memberCard = document.querySelector('[data-membership-card="member"]');
      const radio = memberCard?.querySelector('input[type="radio"]');
      // Also treat "form's hidden id input is the Member variant" as Member-selected, so we
      // intercept even when the radio state hasn't caught up with the form (e.g. Shopify
      // rendered the form from a Member-variant URL and the JS sync hasn't run yet).
      const formVariantId = form.querySelector('input[name="id"]')?.value;
      const memberVariantId = memberCard?.dataset.variantId;
      const memberSelected = (radio?.checked) || (memberVariantId && formVariantId && String(formVariantId) === String(memberVariantId));
      const handle = this.getProductHandleFromUrl();
      const atCap = this.spendCapExceeded || this.effectiveSpendCapExceeded;
      const mightBlockMembership = memberSelected && (
        !this.membershipActive ||
        (this.membershipActive && this.effectiveIs28Day() && (atCap || this.clothingSpend < SPEND_CAP))
      );
      const mightBlockUpgrade = handle === this.membershipUpgradeHandle && (!this.hasPurchased28Day || !this.upgradeWindowEnd || this.upgradeWindowEnd < new Date());
      const mightBlock28Day = handle === this.membership28DayHandle && this.hasPurchased28Day;
      const needsIntervention = mightBlockMembership || mightBlockUpgrade || mightBlock28Day ||
        (memberCard && memberSelected && (atCap || (this.membershipActive && this.effectiveIs28Day() && this.clothingSpend < SPEND_CAP)));

      if (needsIntervention) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        debugLog('[MembershipManager] Click intercepted (will handle)', { mightBlockMembership, mightBlockUpgrade, mightBlock28Day });
        this.handleAddToCart({ target: btn, preventDefault: () => {}, stopPropagation: () => {}, stopImmediatePropagation: () => {} });
      }
    }, true); // Capture phase - run before theme

    document.addEventListener('submit', (e) => {
      const form = e.target;
      const isProductForm = form && (
        (form.getAttribute && form.getAttribute('action') && form.getAttribute('action').includes('/cart/add')) ||
        (form.closest && (form.closest('[data-product-form]') || form.closest('[data-type="add-to-cart-form"]')))
      );
      if (isProductForm) {
        debugLog('[MembershipManager] Form submit intercepted', { formId: form?.id, action: form?.getAttribute?.('action') });
        this.handleAddToCart(e);
        return;
      }
      const isCartForm = form && (form.id === 'cart-form' || form.classList?.contains('cart-form'));
      const isCheckoutSubmit = isCartForm && e.submitter?.getAttribute?.('name') === 'checkout';
      if (isCheckoutSubmit && this.membershipActive && this.effectiveIs28Day() && this.shouldShowUpgradeMessaging() && (this.spendCapExceeded || this.effectiveSpendCapExceeded)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showPopup('upgrade');
      }
    }, true); // Capture phase
  }

  initializeCheckoutBlocking() {
    document.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('[data-membership-checkout-block], #checkout[name="checkout"], button[name="checkout"]');
      if (!btn) return;
      const form = btn.form || document.getElementById('cart-form') || document.querySelector('form.cart-form');
      if (!form) return;
      if (this.membershipActive && this.effectiveIs28Day() && this.shouldShowUpgradeMessaging() && (this.spendCapExceeded || this.effectiveSpendCapExceeded)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showPopup('upgrade');
      }
    }, true);
  }

  updateCheckoutButtonState() {
    const checkoutBtn = document.querySelector('[data-membership-checkout-block], #checkout[name="checkout"]');
    const dynamicButtons = document.querySelector('.additional-checkout-buttons');
    const block = this.membershipActive && this.effectiveIs28Day() && this.shouldShowUpgradeMessaging() && (this.spendCapExceeded || this.effectiveSpendCapExceeded);
    if (checkoutBtn) {
      checkoutBtn.disabled = block;
      checkoutBtn.setAttribute('aria-disabled', block ? 'true' : 'false');
    }
    if (dynamicButtons) {
      dynamicButtons.style.pointerEvents = block ? 'none' : '';
      dynamicButtons.style.opacity = block ? '0.5' : '';
      if (block) {
        dynamicButtons.style.position = 'relative';
        let overlay = dynamicButtons.querySelector('[data-membership-checkout-overlay]');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.setAttribute('data-membership-checkout-overlay', '');
          overlay.style.cssText = 'position:absolute;inset:0;z-index:1;cursor:not-allowed;';
          dynamicButtons.appendChild(overlay);
        }
      } else {
        const overlay = dynamicButtons.querySelector('[data-membership-checkout-overlay]');
        if (overlay) overlay.remove();
      }
    }
  }

  getProductHandleFromUrl() {
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async handleAddToCart(e) {
    if (this.allowNextAddToCart) {
      debugLog('[MembershipManager] handleAddToCart: allowing (allowNextAddToCart), letting submit through');
      this.allowNextAddToCart = false;
      this._lastSubmitAllowedAt = Date.now();
      return;
    }

    // Block any submit within 2.5s of last allowed submit (prevents cascade of duplicate adds)
    const now = Date.now();
    if (this._lastSubmitAllowedAt && now - this._lastSubmitAllowedAt < 2500) {
      debugLog('[MembershipManager] handleAddToCart: BLOCKED (submit cooldown)', { msSinceLast: now - this._lastSubmitAllowedAt });
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return;
    }

    // Prevent concurrent submits (e.g. rapid clicks or duplicate events) from causing multiple adds
    if (this._addInProgress) {
      debugLog('[MembershipManager] handleAddToCart: BLOCKED (_addInProgress)');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return;
    }

    const handle = this.getProductHandleFromUrl();
    const memberCard = document.querySelector('[data-membership-card="member"]');
    const radio = memberCard?.querySelector('input[type="radio"]');
    const form = e.target?.closest?.('form') || (e.target?.tagName === 'FORM' ? e.target : null);
    const formVariantId = form?.querySelector('input[name="id"]')?.value;
    const memberVariantId = memberCard?.dataset.variantId;
    const memberSelected = (radio?.checked) || (memberVariantId && formVariantId && String(formVariantId) === String(memberVariantId));

    // If we might block, preventDefault immediately to stop theme's handler before any async work
    const atCap = this.spendCapExceeded || this.effectiveSpendCapExceeded;
    const mightBlockMembership = memberSelected && (
      !this.membershipActive ||
      (this.membershipActive && this.effectiveIs28Day() && (atCap || this.clothingSpend < SPEND_CAP))
    );
    const mightBlockUpgrade = handle === this.membershipUpgradeHandle && (!this.hasPurchased28Day || !this.upgradeWindowEnd || this.upgradeWindowEnd < new Date());
    const mightBlock28Day = handle === this.membership28DayHandle && this.hasPurchased28Day;
    if (mightBlockMembership || mightBlockUpgrade || mightBlock28Day) {
      debugLog('[MembershipManager] handleAddToCart: preventDefault (mightBlock)', { mightBlockMembership, mightBlockUpgrade, mightBlock28Day, memberSelected });
      this._addInProgress = true;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    if (handle === this.membershipUpgradeHandle) {
      if (!this.hasPurchased28Day) {
        this._addInProgress = false;
        alert("The £20 upgrade is only available to customers who have had a 28-day membership.");
        return;
      }
      const now = new Date();
      if (!this.upgradeWindowEnd || this.upgradeWindowEnd < now) {
        this._addInProgress = false;
        alert("Your upgrade window has expired. Please purchase a new annual membership at full price.");
        return;
      }
    }

    if (handle === this.membership28DayHandle && this.hasPurchased28Day) {
      this._addInProgress = false;
      alert("You've already used your 28-day membership trial. Choose the annual membership instead.");
      return;
    }

    if (!memberCard) {
      debugLog('[MembershipManager] handleAddToCart: no memberCard, allowing (no intervention)');
      return;
    }

    if (!memberSelected) {
      debugLog('[MembershipManager] handleAddToCart: non-member variant selected, allowing');
      return;
    }

    if (atCap) {
      debugLog('[MembershipManager] handleAddToCart: BLOCKED (at cap)');
      this._addInProgress = false;
      if (this.shouldShowUpgradeMessaging()) {
        this.showPopup('upgrade');
      } else {
        alert("Your 28-day membership includes £250 of member-priced clothing. You've reached that limit for this order.");
      }
      return;
    }

    if (this.membershipActive && this.effectiveIs28Day()) {
      if (this.hasUpgradeInCart) {
        debugLog('[MembershipManager] handleAddToCart: allowing (upgrade in cart – no limit)');
        this._addInProgress = false;
        this.allowNextAddToCart = true;
        const form = e.target?.closest?.('form');
        if (form) {
          const formData = new FormData(form);
          const cartItemsComponents = document.querySelectorAll('cart-items-component');
          const sectionIds = [];
          cartItemsComponents.forEach((el) => {
            if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
          });
          if (sectionIds.length) formData.append('sections', sectionIds.join(','));
          const cartAddUrl = (typeof window !== 'undefined' && window.Theme?.routes?.cart_add_url) || '/cart/add.js';
          try {
            const response = await fetch(cartAddUrl, {
              method: 'POST',
              body: formData,
              headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
            });
            const data = await response.json();
            if (data.status) {
              if (typeof alert === 'function') alert(data.message || 'Could not add to cart');
            } else {
              const formId = form.getAttribute('id') || '';
              const productFormEl = form.closest('product-form-component');
              const productId = productFormEl?.dataset?.productId || '';
              const itemCount = Number(formData.get('quantity')) || 1;
              const variantId = String(formData.get('id') || '');
              document.dispatchEvent(new CartAddEvent({}, formId, {
                source: 'membership-manager',
                itemCount,
                productId,
                variantId,
                sections: data.sections
              }));
              this._lastSubmitAllowedAt = Date.now();
              this._disableAddButtonTemporarily(form);
              this.updateRemainingSpendDisplay();
            }
          } catch (err) {
            console.error('[MembershipManager] Cart add failed:', err);
            if (typeof alert === 'function') alert('Could not add to cart. Please try again.');
          }
        }
        return;
      }
      if (this.clothingSpend < SPEND_CAP) {
        const variantPrice = parseFloat(memberCard.dataset.variantPrice) || 0;
        const priceInPounds = variantPrice / 100;
        const wouldExceed = await this.wouldAddExceedSpendCap(priceInPounds);
        if (wouldExceed) {
          debugLog('[MembershipManager] handleAddToCart: BLOCKED (would exceed cap)');
          this._addInProgress = false;
          if (this.shouldShowUpgradeMessaging()) {
            this.showPopup('upgrade');
          } else {
            alert("Your 28-day membership includes £250 of member-priced clothing. You've reached that limit for this order.");
          }
          return;
        }
        debugLog('[MembershipManager] handleAddToCart: allowing (28-day under cap), performing direct fetch');
        this._addInProgress = false;
        this.allowNextAddToCart = true;
        const form = e.target?.closest?.('form');
        if (!form) return;

        const formData = new FormData(form);
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = [];
        cartItemsComponents.forEach((el) => {
          if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
        });
        if (sectionIds.length) formData.append('sections', sectionIds.join(','));

        const cartAddUrl = (typeof window !== 'undefined' && window.Theme?.routes?.cart_add_url) || '/cart/add.js';
        try {
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
          });
          const data = await response.json();

          if (data.status) {
            if (typeof alert === 'function') alert(data.message || 'Could not add to cart');
          } else {
            const formId = form.getAttribute('id') || '';
            const productFormEl = form.closest('product-form-component');
            const productId = productFormEl?.dataset?.productId || '';
            const itemCount = Number(formData.get('quantity')) || 1;
            const variantId = String(formData.get('id') || '');
            document.dispatchEvent(new CartAddEvent({}, formId, {
              source: 'membership-manager',
              itemCount,
              productId,
              variantId,
              sections: data.sections
            }));
            this._lastSubmitAllowedAt = Date.now();
            this._disableAddButtonTemporarily(form);
            this.updateRemainingSpendDisplay();
          }
        } catch (err) {
          console.error('[MembershipManager] Cart add failed:', err);
          if (typeof alert === 'function') alert('Could not add to cart. Please try again.');
        }
        return;
      }
    }

    if (!this.membershipActive) {
      debugLog('[MembershipManager] handleAddToCart: BLOCKED (no membership), showing popup');
      this._addInProgress = false;
      if (this.inUpgradeWindow) {
        this.showPopup('upgrade');
      } else {
        this.showPopup('membership-selection');
      }
      return;
    }

    // Fell through - allowing add (e.g. annual member or non-member variant)
    debugLog('[MembershipManager] handleAddToCart: ALLOWING (fell through - annual or no member card)');
    this._addInProgress = false;
    this._lastSubmitAllowedAt = Date.now();
    this._disableAddButtonTemporarily(e.target);
  }

  _disableAddButtonTemporarily(form) {
    if (!form) return;
    const btn = form.querySelector('[name="add"], [data-add-to-cart], .add-to-cart-button, button[type="submit"]');
    if (btn && !btn.disabled) {
      setTimeout(() => {
        btn.disabled = true;
        setTimeout(() => { btn.disabled = false; }, 2500);
      }, 0);
    }
  }

  async wouldAddExceedSpendCap(newItemPrice) {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const qualifyingTotal = cart.items
        .filter(item => !this.isMembershipItem(item))
        .reduce((sum, item) => sum + item.final_line_price / 100, 0);
      return this.clothingSpend + qualifyingTotal + newItemPrice >= SPEND_CAP;
    } catch {
      return false;
    }
  }

  listenForCartUpdates() {
    // Listen for theme cart update events
    const eventName = ThemeEvents.cartUpdate;
    debugLog('[MembershipManager] Setting up cart update listener for event:', eventName);

    if (!eventName) {
      console.error('[MembershipManager] ERROR: ThemeEvents.cartUpdate is undefined!');
      return;
    }

    this._cartUpdateDebounceTimer = null;
    const onCartChanged = () => {
      if (window.location.pathname.includes('/products/')) {
        this.updateRemainingSpendDisplay().then(() => this.ensureCartUnderCap());
        this.updateUIBasedOnMembership({ skipVariantSync: true });
        this.updateCheckoutButtonState();
      } else {
        this.handleCartUpdate();
      }
    };
    document.addEventListener(eventName, (event) => {
      debugLog('[MembershipManager] CartUpdate event received:', event);
      if (this._cartUpdateDebounceTimer) clearTimeout(this._cartUpdateDebounceTimer);
      this._cartUpdateDebounceTimer = setTimeout(() => {
        this._cartUpdateDebounceTimer = null;
        onCartChanged();
      }, 300);
    });
    document.addEventListener('cart:refresh', () => {
      if (this._cartUpdateDebounceTimer) clearTimeout(this._cartUpdateDebounceTimer);
      this._cartUpdateDebounceTimer = setTimeout(() => {
        this._cartUpdateDebounceTimer = null;
        onCartChanged();
      }, 300);
    });

    if (window.location.pathname.includes('/cart')) {
      debugLog('[MembershipManager] On cart page - validating immediately');
      this.validateCartNow();
      this.updateCartUpgradePrompt();
    }
  }

  async updateCartUpgradePrompt() {
    const prompt = document.querySelector('[data-membership-cart-upgrade-prompt]');
    if (!prompt || !this.shouldShowUpgradeMessaging() || !(this.spendCapExceeded || this.effectiveSpendCapExceeded)) {
      if (prompt) prompt.style.display = 'none';
      return;
    }
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const hasMemberItems = cart.items.some(item => {
        if (this.isMembershipItem(item)) return false;
        return (item.variant_title && item.variant_title.includes('Member') && !item.variant_title.includes('Non-Member')) ||
          (item.options_with_values && item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Member'));
      });
      const hasUpgrade = cart.items.some(item => item.handle === this.membershipUpgradeHandle);
      prompt.style.display = hasMemberItems && !hasUpgrade ? '' : 'none';
    } catch {
      if (prompt) prompt.style.display = 'none';
    }
  }

  async handleCartUpdate() {
    debugLog('[MembershipManager] handleCartUpdate called');
    const wasActive = this.membershipActive;
    const isActive = await this.checkCartForMembership();

    debugLog('[MembershipManager] wasActive:', wasActive, 'isActive:', isActive);

    this.membershipActive = isActive;
    sessionStorage.setItem('membershipActive', isActive ? 'true' : 'false');

    // If membership status changed, clear cart and notify user
    if (wasActive !== isActive) {
      debugLog('[MembershipManager] Membership status changed!');
      if (!wasActive && isActive) {
        await this.clearCartWithMessage('Membership added! Please re-add your items to get member pricing.');
      } else if (wasActive && !isActive) {
        debugLog('[MembershipManager] Membership removed - clearing cart');
        await this.clearCartWithMessage('Membership removed. Please re-add your items at regular pricing.');
      }
    } else {
      // Status didn't change, but validate cart has correct variants
      debugLog('[MembershipManager] Status unchanged - validating cart');
      await this.validateCartNow();
    }

    this.updateRemainingSpendDisplay();
    this.updateUIBasedOnMembership({ skipVariantSync: true });
    if (window.location.pathname.includes('/cart')) {
      this.updateCartUpgradePrompt();
    }
    this.updateCheckoutButtonState();
    await this.convertOverLimitMemberVariantsToNonMember();
    this.ensureCartUnderCap();
  }

  async updateRemainingSpendDisplay() {
    if (!this.membershipActive) return;
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      this.has28DayMembershipInCart = cart.items.some(item => item.handle === this.membership28DayHandle);
      this.hasUpgradeInCart = cart.items.some(item => item.handle === this.membershipUpgradeHandle);
      if (!this.effectiveIs28Day()) return;
      const baseSpend = (this.has28DayMembershipInCart && this.membershipType !== '28-day') ? 0 : this.clothingSpend;
      const qualifyingTotal = cart.items
        .filter(item => !this.isMembershipItem(item))
        .reduce((sum, item) => sum + item.final_line_price / 100, 0);
      const effectiveSpend = baseSpend + qualifyingTotal;
      const remaining = Math.max(0, SPEND_CAP - effectiveSpend);
      const atCap = !this.hasUpgradeInCart && effectiveSpend >= SPEND_CAP;
      this.effectiveSpendCapExceeded = atCap;
      const els = document.querySelectorAll('[data-membership-remaining-spend]');
      if (els.length) {
        els.forEach(el => {
          el.textContent = this.hasUpgradeInCart
            ? 'Upgrade in cart – no spending limit'
            : (atCap
              ? (this.shouldShowUpgradeMessaging() ? 'Limit reached. Upgrade to continue.' : 'Limit reached for this order.')
              : `£${remaining.toFixed(2)} remaining of your £250 allowance`);
          el.classList.toggle('membership-selector__remaining--cap', atCap);
        });
      }
      this.updateCheckoutButtonState();
    } catch (e) {
      debugLog('[MembershipManager] Could not update remaining spend display:', e);
    }
  }

  async ensureCartUnderCap() {
    if (!this.membershipActive) return;
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const hasUpgrade = cart.items.some(i => i.handle === this.membershipUpgradeHandle);
      if (hasUpgrade) return;
      const has28Day = cart.items.some(i => i.handle === this.membership28DayHandle);
      const effectiveIs28Day = this.membershipType === '28-day' || has28Day;
      if (!effectiveIs28Day) return;
      const baseSpend = (has28Day && this.membershipType !== '28-day') ? 0 : (this.clothingSpend || 0);
      const qualifying = cart.items
        .map((item, idx) => ({ item, line: idx + 1 }))
        .filter(({ item }) => !this.isMembershipItem(item));
      const qualifyingTotal = qualifying.reduce((s, { item }) => s + item.final_line_price / 100, 0);
      const headroom = Math.max(0, SPEND_CAP - baseSpend);
      if (qualifyingTotal <= headroom) return;
      const itemsWithTotal = qualifying.map(({ item, line }) => ({
        line,
        key: item.key,
        qty: item.quantity,
        unitPrice: item.quantity > 0 ? item.final_line_price / (100 * item.quantity) : 0,
        lineTotal: item.final_line_price / 100
      })).filter(x => x.unitPrice > 0);
      itemsWithTotal.sort((a, b) => b.lineTotal - a.lineTotal);
      let currentTotal = qualifyingTotal;
      for (const x of itemsWithTotal) {
        if (currentTotal <= headroom) break;
        const otherTotal = currentTotal - x.lineTotal;
        const amountForThis = headroom - otherTotal;
        const maxQty = Math.max(0, Math.floor(amountForThis / x.unitPrice));
        if (x.qty > maxQty) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ line: x.line, quantity: maxQty })
          });
          currentTotal = otherTotal + maxQty * x.unitPrice;
          debugLog('[MembershipManager] ensureCartUnderCap: reduced line', x.line, 'to', maxQty);
        }
      }
    } catch (e) {
      debugLog('[MembershipManager] ensureCartUnderCap failed:', e);
    }
  }

  /**
   * Convert Member-variant lines that exceed the £250 allowance to Non-Member variant,
   * so both price and displayed variant label match (items over limit are charged regular price).
   */
  async convertOverLimitMemberVariantsToNonMember() {
    if (!this.membershipActive) return;
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const hasUpgrade = cart.items.some(i => i.handle === this.membershipUpgradeHandle);
      if (hasUpgrade) return;
      const has28Day = cart.items.some(i => i.handle === this.membership28DayHandle);
      const effectiveIs28Day = this.membershipType === '28-day' || has28Day;
      if (!effectiveIs28Day) return;
      const baseSpend = (has28Day && this.membershipType !== '28-day') ? 0 : (this.clothingSpend || 0);
      const headroom = Math.max(0, SPEND_CAP - baseSpend);

      const isMemberVariant = (item) =>
        (item.variant_title && item.variant_title.includes('Member') && !item.variant_title.includes('Non-Member')) ||
        (item.options_with_values && item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Member'));

      const qualifying = cart.items
        .map((item, idx) => ({ item, line: idx + 1 }))
        .filter(({ item }) => !this.isMembershipItem(item) && isMemberVariant(item));

      let runningTotal = 0;
      const toConvert = [];
      for (const { item, line } of qualifying) {
        const lineTotal = item.final_line_price / 100;
        if (runningTotal + lineTotal <= headroom) {
          runningTotal += lineTotal;
        } else {
          toConvert.push({ item, line });
        }
      }
      if (toConvert.length === 0) return;

      // Process from highest line index first so removals don't shift indices
      toConvert.sort((a, b) => b.line - a.line);

      for (const { item, line } of toConvert) {
        let nonMemberTitle = null;
        if (item.variant_title) {
          const withRegex = item.variant_title.replace(/ \/ Member$/, ' / Non-Member');
          nonMemberTitle = withRegex !== item.variant_title ? withRegex : item.variant_title.replace('Member', 'Non-Member');
        }
        if (!nonMemberTitle || nonMemberTitle === item.variant_title) continue;

        const product = await fetch(`/products/${item.handle}.js`).then(r => r.json());
        let nonMemberVariant = product.variants.find(v => v.title === nonMemberTitle);
        if (!nonMemberVariant && item.options_with_values) {
          const membershipOpt = item.options_with_values.find(o => o.name === 'Membership');
          if (membershipOpt && membershipOpt.value === 'Member') {
            nonMemberVariant = product.variants.find(v => {
              const opt = v.options_with_values?.find(o => o.name === 'Membership');
              if (!opt || opt.value !== 'Non-Member') return false;
              return item.options_with_values.every(o => {
                if (o.name === 'Membership') return true;
                const vOpt = v.options_with_values?.find(vo => vo.name === o.name);
                return vOpt && vOpt.value === o.value;
              });
            });
          }
        }
        if (!nonMemberVariant) continue;

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ line, quantity: 0 })
        });
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: nonMemberVariant.id, quantity: item.quantity })
        });
        debugLog('[MembershipManager] convertOverLimitMemberVariantsToNonMember: converted line', line, item.title);
      }
    } catch (e) {
      debugLog('[MembershipManager] convertOverLimitMemberVariantsToNonMember failed:', e);
    }
  }

  async validateCartNow() {
    // Check if we should skip validation (just added membership from popup)
    const skipValidationTime = sessionStorage.getItem('skipValidation');
    if (skipValidationTime) {
      const elapsed = Date.now() - parseInt(skipValidationTime);
      if (elapsed < 5000) { // Skip for 5 seconds
        return;
      }
      sessionStorage.removeItem('skipValidation');
    }

    await this.checkForInvalidMembershipProductsInCart();

    if (this.membershipActive) {
      // 28-day members at spend cap: convert over-limit Member variants to Non-Member (label matches price)
      await this.convertOverLimitMemberVariantsToNonMember();
      // 28-day members at spend cap are allowed to have non-member items (regular pricing only)
      if (!(this.spendCapExceeded || this.effectiveSpendCapExceeded)) {
        await this.checkForWrongVariantsWithMembership();
      }
    } else {
      await this.checkForMemberVariantsWithoutMembership();
    }
  }

  async clearCartWithMessage(message) {
    try {
      debugLog('[MembershipManager] Clearing cart with message:', message);
      await fetch('/cart/clear.js', { method: 'POST' });
      alert(message);
      // Immediate reload for speed
      window.location.reload(true);
    } catch (error) {
      console.error('[ERROR] Clear cart failed:', error);
      window.location.reload(true);
    }
  }

  async checkForInvalidMembershipProductsInCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      for (const item of cart.items) {
        if (item.handle === this.membership28DayHandle && this.hasPurchased28Day) {
          await this.clearCartWithMessage("You've already used your 28-day membership trial. The 28-day option has been removed from your cart.");
          return;
        }
        if (item.handle === this.membershipUpgradeHandle) {
          if (!this.hasPurchased28Day) {
            await this.clearCartWithMessage("The £20 upgrade is only available to customers who have had a 28-day membership. Cart has been cleared.");
            return;
          }
          const now = new Date();
          if (!this.upgradeWindowEnd || this.upgradeWindowEnd < now) {
            await this.clearCartWithMessage("Your upgrade window has expired. Please purchase a new annual membership at full price. Cart has been cleared.");
            return;
          }
        }
      }
    } catch (error) {
      console.error('[ERROR] Check invalid membership products failed:', error);
    }
  }

  async checkForMemberVariantsWithoutMembership() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      for (const item of cart.items) {
        if (this.isMembershipItem(item)) {
          continue;
        }

        const hasMemberOption = item.variant_title &&
                               (item.variant_title.includes('Member') && !item.variant_title.includes('Non-Member'));

        const hasMemberInOptions = item.options_with_values &&
                                  item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Member');

        if (hasMemberOption || hasMemberInOptions) {
          await this.clearCartWithMessage('Member-priced items cannot be purchased without membership. Cart has been cleared. Please add membership first or select regular-priced items.');
          return;
        }
      }
    } catch (error) {
      console.error('[ERROR] Check member variants failed:', error);
    }
  }

  async checkForWrongVariantsWithMembership() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      for (const item of cart.items) {
        if (this.isMembershipItem(item)) {
          continue;
        }

        const hasNonMemberOption = item.variant_title &&
                                   item.variant_title.includes('Non-Member');

        const hasNonMemberInOptions = item.options_with_values &&
                                     item.options_with_values.some(opt => opt.name === 'Membership' && opt.value === 'Non-Member');

        if (hasNonMemberOption || hasNonMemberInOptions) {
          await this.clearCartWithMessage('You have membership! Cart has been cleared. Please re-add your items to get member pricing.');
          return;
        }
      }
    } catch (error) {
      console.error('[ERROR] Check wrong variants failed:', error);
    }
  }

  updateUIBasedOnMembership(opts = {}) {
    const { skipVariantSync = false } = opts;
    const allCards = document.querySelectorAll('[data-membership-card]');

    // When 28-day member at spend cap: enable non-member, disable member (regular pricing only)
    const atCap = this.spendCapExceeded || this.effectiveSpendCapExceeded;
    if (this.membershipActive && atCap) {
      allCards.forEach(card => {
        if (card.dataset.membershipCard === 'non-member') {
          card.classList.remove('membership-pill--disabled');
          card.classList.add('membership-pill--selected');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = false;
            radio.checked = true;
          }
          if (!skipVariantSync) {
            const variantId = card.dataset.variantId;
            if (variantId) this.updateSelectedVariant(variantId);
          }
        } else if (card.dataset.membershipCard === 'member') {
          card.classList.remove('membership-pill--locked', 'membership-pill--selected');
          card.classList.add('membership-pill--cap-exceeded');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = true;
            radio.checked = false;
          }
        }
      });
      return;
    }

    allCards.forEach(card => {
      if (this.membershipActive) {
        if (card.dataset.membershipCard === 'non-member') {
          // Disable non-member card
          card.classList.add('membership-pill--disabled');
          card.classList.remove('membership-pill--selected');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = true;
            radio.checked = false;
          }
        } else if (card.dataset.membershipCard === 'member') {
          // Lock and auto-select member card
          card.classList.add('membership-pill--locked', 'membership-pill--selected');
          card.classList.remove('membership-pill--cap-exceeded');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.disabled = true;
            radio.checked = true;
          }
          if (!skipVariantSync) {
            const variantId = card.dataset.variantId;
            if (variantId) this.updateSelectedVariant(variantId);
          }
        }
      } else {
        // Reset to normal state
        card.classList.remove('membership-pill--disabled', 'membership-pill--locked', 'membership-pill--cap-exceeded');
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.disabled = false;
      }
    });

    // Non-member sync: Shopify renders the form's hidden id input from the URL's ?variant=
    // param, which may point at the Member variant. The Liquid pills now correctly default
    // to Non-Member for non-members, but the form input still holds the Member id. Sync it
    // so Add to Cart submits the Non-Member variant.
    if (!this.membershipActive && !skipVariantSync) {
      const nonMemberCard = document.querySelector('[data-membership-card="non-member"]');
      const memberCard = document.querySelector('[data-membership-card="member"]');
      const nonMemberVariantId = nonMemberCard?.dataset.variantId;
      const memberVariantId = memberCard?.dataset.variantId;
      const variantHidden = document.querySelector('input[type="hidden"][name="id"]');
      if (nonMemberVariantId && memberVariantId && variantHidden && variantHidden.value === memberVariantId) {
        this.updateSelectedVariant(nonMemberVariantId);
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.membershipManager = new MembershipManager();
  });
} else {
  window.membershipManager = new MembershipManager();
}
